import React, { useCallback, useEffect, useRef, useState } from "react";
import { usersApi } from "@services/api/users";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";

interface SignatureUploadWidgetProps {
  onSignatureUpdated?: (signatureUrl: string) => void;
  /** When true, the widget is visually emphasised (bigger, centered CTA). */
  prominent?: boolean;
}

type Tab = "draw" | "upload";

export default function SignatureUploadWidget({
  onSignatureUpdated,
  prominent = false,
}: SignatureUploadWidgetProps) {
  const userData = useDataStore((state) => state.userData);
  const fetchUserData = useDataStore((state) => state.fetchUserData);
  const [uploading, setUploading] = useState(false);
  const preview = userData?.infos?.signature_id || "";

  const [tab, setTab] = useState<Tab>(preview ? "upload" : "draw");

  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Canvas drawing
  useEffect(() => {
    if (tab !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const getCoords = (clientX: number, clientY: number) => {
      const r = canvas.getBoundingClientRect();
      return {
        x: (clientX - r.left) * (canvas.width / r.width),
        y: (clientY - r.top) * (canvas.height / r.height),
      };
    };

    const startDraw = (x: number, y: number) => {
      drawingRef.current = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const draw = (x: number, y: number) => {
      if (!drawingRef.current) return;
      ctx.lineTo(x, y);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      setHasDrawn(true);
    };
    const stopDraw = () => {
      drawingRef.current = false;
    };

    const onMouseDown = (e: MouseEvent) => {
      const { x, y } = getCoords(e.clientX, e.clientY);
      startDraw(x, y);
    };
    const onMouseMove = (e: MouseEvent) => {
      const { x, y } = getCoords(e.clientX, e.clientY);
      draw(x, y);
    };
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      const { x, y } = getCoords(t.clientX, t.clientY);
      startDraw(x, y);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      const { x, y } = getCoords(t.clientX, t.clientY);
      draw(x, y);
    };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", stopDraw);
    canvas.addEventListener("mouseleave", stopDraw);
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", stopDraw);

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", stopDraw);
      canvas.removeEventListener("mouseleave", stopDraw);
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", stopDraw);
    };
  }, [tab]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const saveDrawnSignature = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !userData?.id) return;
    setUploading(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      if (!blob) throw new Error("Failed to export canvas");
      const file = new File([blob], "signature.png", { type: "image/png" });
      const response = await usersApi.uploadSignature(userData.id, file);
      await fetchUserData(userData.id);
      onSignatureUpdated?.(response.signature_url);
      showNotification({
        type: "success",
        message: "Signature enregistrée",
      });
      setHasDrawn(false);
    } catch {
      showNotification({
        type: "error",
        message: "Échec de l'enregistrement de la signature",
      });
    } finally {
      setUploading(false);
    }
  }, [userData?.id, fetchUserData, onSignatureUpdated]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !userData?.id) return;
    const file = e.target.files[0];
    if (!file.type.startsWith("image/")) {
      showNotification({
        type: "error",
        message: "Veuillez sélectionner une image",
      });
      return;
    }
    setUploading(true);
    try {
      const response = await usersApi.uploadSignature(userData.id, file);
      await fetchUserData(userData.id);
      onSignatureUpdated?.(response.signature_url);
      showNotification({
        type: "success",
        message: "Signature mise à jour",
      });
    } catch {
      showNotification({
        type: "error",
        message: "Échec du téléchargement de la signature",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border border-neutral-200 ${
        prominent && !preview ? "bg-blue-50 border-blue-200 p-5" : "bg-neutral-50 p-4"
      }`}
    >
      {prominent && !preview && (
        <div className="text-h3 font-semibold text-blue-800 mb-1">
          Vous n'avez pas encore de signature
        </div>
      )}

      <div className="text-h4 font-medium text-neutral-700">
        {preview ? "Votre signature" : "Ajoutez votre signature pour signer"}
      </div>

      {/* Existing preview */}
      {preview && (
        <div className="flex flex-col items-center gap-2">
          <img
            src={preview}
            alt="Signature"
            className="max-h-20 border rounded bg-white p-2"
          />
          <button
            onClick={() => {
              setTab("draw");
            }}
            className="text-h5 text-blue-600 hover:underline"
          >
            Modifier la signature
          </button>
        </div>
      )}

      {/* Tabs (shown when no preview, or when user clicked "Modifier") */}
      {(!preview || tab === "draw" || tab === "upload") && !preview && (
        <>
          <div className="flex flex-row border-b border-neutral-200">
            <button
              onClick={() => setTab("draw")}
              className={`flex-1 py-2 text-h4 font-medium border-b-2 transition-colors ${
                tab === "draw"
                  ? "border-neutral-800 text-neutral-800"
                  : "border-transparent text-neutral-400 hover:text-neutral-600"
              }`}
            >
              Dessiner
            </button>
            <button
              onClick={() => setTab("upload")}
              className={`flex-1 py-2 text-h4 font-medium border-b-2 transition-colors ${
                tab === "upload"
                  ? "border-neutral-800 text-neutral-800"
                  : "border-transparent text-neutral-400 hover:text-neutral-600"
              }`}
            >
              Importer
            </button>
          </div>

          {tab === "draw" && (
            <div className="flex flex-col gap-2">
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                className="w-full border rounded-lg bg-white cursor-crosshair"
                style={{ touchAction: "none" }}
              />
              <div className="flex flex-row gap-2">
                <button
                  onClick={clearCanvas}
                  disabled={!hasDrawn || uploading}
                  className="flex-1 px-3 py-1.5 rounded-lg text-h4 border border-neutral-200 text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
                >
                  Effacer
                </button>
                <button
                  onClick={saveDrawnSignature}
                  disabled={!hasDrawn || uploading}
                  className="flex-1 px-3 py-1.5 rounded-lg text-h4 bg-neutral-800 text-white hover:bg-neutral-700 disabled:opacity-40"
                >
                  {uploading ? "Enregistrement…" : "Valider"}
                </button>
              </div>
            </div>
          )}

          {tab === "upload" && (
            <div className="flex flex-col gap-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="sig-upload-widget"
                disabled={uploading}
              />
              <label
                htmlFor="sig-upload-widget"
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-neutral-400 transition ${
                  uploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <div className="flex flex-col items-center gap-2 text-neutral-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <span className="text-h4">
                    {uploading
                      ? "Téléchargement..."
                      : "Cliquez pour importer une image de signature"}
                  </span>
                </div>
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
}
